import type { GitHubConfig } from '../types';
import { githubMediaProxyUrl } from './githubMediaProxyUrl';

function branchName(config: GitHubConfig): string {
  return config.branch.trim() || 'main';
}

function repoRelativePath(repoPath: string): string {
  return repoPath.replace(/^\//, '');
}

/** CDN URL for a blob on the default branch (works for Git LFS objects GitHub hosts). */
export function githubMediaBlobUrl(config: GitHubConfig, repoPath: string): string {
  const branch = encodeURIComponent(branchName(config));
  const path = repoRelativePath(repoPath);
  return `https://media.githubusercontent.com/media/${config.owner}/${config.repo}/${branch}/${path}`;
}

/**
 * github.com/raw resolves Git LFS pointers to real bytes (unlike raw.githubusercontent.com).
 * @see https://github.com/USERNAME/REPO/raw/BRANCH/PATH/TO/VIDEO.mp4
 */
export function githubRawMediaUrl(config: GitHubConfig, repoPath: string): string {
  const path = repoRelativePath(repoPath);
  return `https://github.com/${config.owner}/${config.repo}/raw/${branchName(config)}/${path}`;
}

/**
 * jsDelivr serves with CORS * and reliable Range — better for Firefox Android streaming.
 * @see https://cdn.jsdelivr.net/gh/USERNAME/REPO@BRANCH/PATH/TO/VIDEO.mp4
 */
export function jsDelivrMediaUrl(config: GitHubConfig, repoPath: string): string {
  const path = repoRelativePath(repoPath);
  return `https://cdn.jsdelivr.net/gh/${config.owner}/${config.repo}@${branchName(config)}/${path}`;
}

/** True when the repo is not world-readable (default for this app). */
export function repoIsPrivate(config: GitHubConfig): boolean {
  return config.private !== false;
}

/**
 * raw.githubusercontent.com with PAT query token (from FE settings).
 * Video elements cannot send Authorization headers; token in URL is required for private repos.
 */
export function githubAuthenticatedRawUrl(config: GitHubConfig, repoPath: string): string {
  const branch = encodeURIComponent(branchName(config));
  const path = repoRelativePath(repoPath);
  const url = new URL(
    `https://raw.githubusercontent.com/${config.owner}/${config.repo}/${branch}/${path}`
  );
  const pat = config.pat.trim();
  if (pat) url.searchParams.set('token', pat);
  return url.toString();
}

/** PAT-backed stream URLs for private repos (same-origin proxy + tokenized raw). */
export function authenticatedVideoStreamUrlCandidates(
  config: GitHubConfig,
  repoPath: string,
  /**
   * Firefox: same-origin /api/github-media only — PAT raw HTTPS gets MediaError 4
   * in embedded video (open-in-new-tab also uses the proxy, not PAT raw).
   */
  firefoxProxyOnly = false
): string[] {
  if (!repoIsPrivate(config)) return [];
  const proxy = githubMediaProxyUrl(config, repoPath);
  const raw = githubAuthenticatedRawUrl(config, repoPath);
  return firefoxProxyOnly ? [proxy] : [proxy, raw];
}

/** Ordered public HTTPS stream candidates; empty for private repos (no PAT on CDN). */
export function videoStreamUrlCandidates(
  config: GitHubConfig,
  repoPath: string,
  firefoxFirst = false
): string[] {
  if (repoIsPrivate(config)) return [];

  const jsdelivr = jsDelivrMediaUrl(config, repoPath);
  const raw = githubRawMediaUrl(config, repoPath);
  const media = githubMediaBlobUrl(config, repoPath);
  const ordered = firefoxFirst
    ? [jsdelivr, raw, media]
    : [media, raw, jsdelivr];
  return [...new Set(ordered)];
}

/** True for GitHub-hosted media/CDN URLs that must not receive API Authorization headers. */
export function isGithubCdnMediaUrl(url: string): boolean {
  try {
    const host = new URL(url).hostname.toLowerCase();
    return (
      host === 'raw.githubusercontent.com' ||
      host === 'objects.githubusercontent.com' ||
      host === 'cdn.jsdelivr.net' ||
      host === 'github.com' ||
      host.endsWith('.githubusercontent.com')
    );
  } catch {
    return false;
  }
}
